import React, { useContext, useId, useState } from "react";
import { ToolsContext, ToolsDispatchContext } from "../../ToolsContext";
import { useMutateGtfsSegment, useQueryGtfsResource, useQueryGtfsSegment } from "../../Queries";
import { ProgressSpinner } from "primereact/progressspinner";
import { StopIdPropertyDropdown } from "../../components/StopIdPropertyDropdown";
import { PropertyInput } from "../../components/PropertyInput";
import { Button } from "primereact/button";
import { gtfsTimeStringToSeconds, secondsToGtfsTimeString } from "../../helpers/gtfsDateUtils";
import { RouteTypePropertyDropdown } from "../../components/RouteTypePropertyDropdown";
import { OverlayContext } from "../../OverlaysContext";
import { Message } from 'primereact/message';
import { ScrollPanel } from "primereact/scrollpanel";



export function Segments() {
    const tool = useContext(ToolsContext);
    const toolDispatch = useContext(ToolsDispatchContext);
    const { data: segment, isPendingS, errorS } = useQueryGtfsSegment(
        (tool.selection) ? tool.selection.route_type : 3,
        (tool.selection) ? tool.selection.from_stop_id : null,
        (tool.selection) ? tool.selection.to_stop_id : null
    );
    const { data: stopsList, isPending, error } = useQueryGtfsResource('stops');

    let panelHeight = '82vh';

    if (isPending || !stopsList) {
        return (
            <ProgressSpinner className="flex justify-content-center" />
        );
    }

    if (error) {
        return (
            <>
                <i className="fa-solid fa-face-frown fa-5x"></i><br />
                Wystąpił błąd ładowania przystanków: {error.message}
            </>
        )
    }

    const handleSelectStop = (propertyName, value) => {
        let newEndpoints = (tool.selection) ? { ...tool.selection } : { route_type: 3, from_stop_id: null, to_stop_id: null };
        newEndpoints[propertyName] = value;
        toolDispatch({
            type: 'changed',
            newTool: {
                ...tool,
                selection: newEndpoints, // zaznacz trasę
                propertyEditor: {
                    edit: newEndpoints
                }
            }
        });
    }

    return (
        <>
            <ScrollPanel style={{ height: panelHeight, width: '100%' }}>
                <div className="formgrid grid p-2">
                    <div className="field col-12">
                        <RouteTypePropertyDropdown
                            propertyName='route_type'
                            initialVal={segment ? segment.route_type : 3}
                            label="Trakcja"
                            onDone={(propertyName, value) => {
                                handleSelectStop(propertyName, value);
                            }}
                        />
                    </div>
                    <div className='field col-12'>
                        <StopIdPropertyDropdown
                            initialVal={(tool.selection && tool.selection.from_stop_id) && tool.selection.from_stop_id}
                            propertyName='from_stop_id'
                            label="Od przystanku:"
                            options={Object.values(stopsList)}
                            onDone={(propertyName, value) => {
                                handleSelectStop(propertyName, value);
                            }}
                        />
                    </div>
                    <div className='field col-12'>
                        <StopIdPropertyDropdown
                            propertyName='to_stop_id'
                            initialVal={(tool.selection && tool.selection.to_stop_id) && tool.selection.to_stop_id}
                            label="Do przystanku:"
                            options={Object.values(stopsList)}
                            onDone={(propertyName, value) => {
                                handleSelectStop(propertyName, value);
                            }}
                        />
                    </div>

                {
                    (tool.selection && tool.selection.from_stop_id && tool.selection.to_stop_id) ?
                        <SegmentEditOrCreate segment={segment} />
                        :
                        <>Wybierz powyżej</>
                }
                </div>
            </ScrollPanel>
        </>
    );
}

function SegmentEditOrCreate({ segment }) {
    return (
        <>
            {(segment) ? <SegmentPropertyEditor segment={segment} /> : <SegmentCreate />}
        </>
    )
}

function SegmentCreate() {
    const tool = useContext(ToolsContext);
    const toolDispatch = useContext(ToolsDispatchContext);
    const segmentMutation = useMutateGtfsSegment();

    if (tool.selection.from_stop_id === tool.selection.to_stop_id) {
        return <Message
            className="m-2"
            severity="error"
            text="Odcinek nie może kończyć się i zaczynać w tym samym miejscu"
        />
    }

    const handleCreateSegment = () => {
        const payload = {
            query: {
                from_stop_id: tool.selection.from_stop_id,
                to_stop_id: tool.selection.to_stop_id,
                route_type: tool.selection.route_type
            },
            fields: {
                default_travel_time: '00:00:00',
                length: '0'
            }
        };

        segmentMutation.mutate(payload);  // próbujemy przeprowadzić mutację
    }

    const handleCreateSegmentFromMap = () => {
        const payload = {
            query: {
                from_stop_id: tool.selection.from_stop_id,
                to_stop_id: tool.selection.to_stop_id,
                route_type: tool.selection.route_type
            },
            fields: {
                action: 'fromMap'
            }
        };

        segmentMutation.mutate(payload);  // próbujemy przeprowadzić mutację
    }

    return (
        <div className="formgrid grid p-2">
            <div className="field col-12">
                <Button
                    outlined
                    label="Utwórz odcinek między tymi przystankami"
                    icon="fa-solid fa-square-plus"
                    className="w-full"
                    onClick={handleCreateSegment}
                    severity="success"
                />
            </div>
            <div className="field col-12">
                <Button
                    outlined
                    label="Utwórz odcinek wg danych z mapy"
                    icon="fa-solid fa-map"
                    className="w-full"
                    onClick={handleCreateSegmentFromMap}
                    severity="help"
                />
            </div>
        </div>
    )
}


function SegmentPropertyEditor({ segment }) {
    const id = useId();
    const handleOverlays = useContext(OverlayContext);
    const [modifiedValues, setModifiedValues] = useState(segment);
    const [isMutationPending, setIsMutationPending] = useState(false);
    const [validationErrors, setValidationErrors] = useState(false);
    const segmentMutation = useMutateGtfsSegment();


    if (!segment) return (<>Brak segmentu</>);
    if (!modifiedValues) return (<>?</>);

    const handleInput = (propertyName, value) => {
        let newValues = { ...modifiedValues };
        newValues[propertyName] = value;
        setModifiedValues(newValues);
    }

    function handleInputCancel() {
        setModifiedValues(segment);
    }


    const handleInputDone = async (propertyName, value) => {
        if (value === segment[propertyName]) {
            return; // Nie wysyłamy zmian na serwer, jeżeli nowa wartość jest taka jak już zapisana do serwera.
        }

        const payload = {
            query: {
                from_stop_id: tool.selection.from_stop_id,
                to_stop_id: tool.selection.to_stop_id,
                route_type: tool.selection.route_type
            },
            fields: {}
        };
        payload.fields[propertyName] = value;
        setIsMutationPending(true);

        try {
            const asyncMutation = await segmentMutation.mutateAsync(payload);  // próbujemy przeprowadzić mutację
            // wykasuj ewentualne istniejące błędy walidacji z UI, skoro wszystko poszło ok
            if (validationErrors[propertyName]) {
                let updatedValidationErrors = { ...validationErrors };
                updatedValidationErrors[propertyName] = false;
                setValidationErrors({
                    ...updatedValidationErrors
                });
            }
        } catch (error) {
            const errorCode = error.message.slice(0, 3);
            if (errorCode == '422') {
                //obsługa błędów walidacji
                let updatedValidationErrors = { ...validationErrors };
                updatedValidationErrors[propertyName] = Object.values(error.cause.validation[propertyName]).join(', ');
                setValidationErrors({
                    ...updatedValidationErrors
                });
            } else {
                //obsługa pozostałych błędów
                handleOverlays('toast', {
                    severity: "error",
                    summary: 'Błąd ' + errorCode,
                    detail: (typeof error.cause === 'undefined' || typeof error.cause.message === 'undefined') ? 'Wystąpił błąd. Czynność nie mogła zostać zakończona powodzeniem. ' + error.message : error.cause.message
                });
                console.log(error);
            }
        } finally {
            setIsMutationPending(false);
        }

    }

    return (
        <div className="formgrid grid p-2">
            <h3 className="col-12">Domyślne parametry odcinka</h3>
            <div className="field col-4">
                <PropertyInput numeric={true}
                    propertyName='length'
                    initialVal={segment.length}
                    label="Odległość"
                    id={id}
                    value={modifiedValues.length}
                    onDone={handleInputDone}
                    onChange={handleInput}
                    onCancel={handleInputCancel}
                    pending={isMutationPending}
                    suffix=" km"
                />
            </div>
            <div className="field col-4">
                <PropertyInput numeric={true}
                    propertyName='default_travel_time'
                    initialVal={gtfsTimeStringToSeconds(segment.default_travel_time) / 60}
                    label="Czas przej."
                    id={id}
                    value={gtfsTimeStringToSeconds(modifiedValues.default_travel_time) / 60}
                    onDone={(propertyName, value) => handleInputDone(propertyName, secondsToGtfsTimeString(value * 60))}
                    onChange={(propertyName, value) => handleInput(propertyName, secondsToGtfsTimeString(value * 60))}
                    onCancel={handleInputCancel}
                    pending={isMutationPending}
                    suffix=" min."
                />
            </div>
            <div className="field col-4">
                <PropertyInput numeric={true}
                    propertyName='avs'
                    initialVal={
                        (segment.length && segment.default_travel_time) 
                        ? segment.length / (gtfsTimeStringToSeconds(segment.default_travel_time) / 3600)
                        : null
                    }
                    label="Średnia prędk."
                    id={id}
                    value={
                        (modifiedValues.length && modifiedValues.default_travel_time) 
                        ? modifiedValues.length / (gtfsTimeStringToSeconds(modifiedValues.default_travel_time) / 3600)
                        : null
                    }
                    onChange={(propertyName,value) => {
                        let timeH = modifiedValues.length / value;
                        let timeS = timeH * 3600;
                        if (value) setModifiedValues({
                            ...modifiedValues,
                            default_travel_time: secondsToGtfsTimeString(timeS)
                        })
                    }}
                    onDone={() => {
                        handleInputDone('default_travel_time',modifiedValues.default_travel_time)
                    }}
                    onCancel={handleInputCancel}
                    pending={isMutationPending}
                    suffix=" km/h"
                />
            </div>            
        </div>
    )
}